import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Stack,
    Alert,
    Skeleton,
    Tooltip,
    InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InventoryIcon from '@mui/icons-material/Inventory2';
import { useNotify } from 'react-admin';
import api from '../services/api';
import {
    productService,
    Product,
    ProductPrice,
    ProductVisibility,
    PriceType,
} from '../services/productService';

interface CourseOption {
    id: number;
    title: string;
}

interface ProductFormState {
    courseId: number | '';
    title: string;
    shortDescription: string;
    description: string;
    visibility: ProductVisibility;
    isActive: boolean;
    priceType: Extract<PriceType, 'one_time' | 'free'>;
    amount: string;
}

const EMPTY_FORM: ProductFormState = {
    courseId: '',
    title: '',
    shortDescription: '',
    description: '',
    visibility: 'public',
    isActive: true,
    priceType: 'one_time',
    amount: '0.00',
};

const VISIBILITY_LABELS: Record<ProductVisibility, string> = {
    public: 'Öffentlich',
    unlisted: 'Nicht gelistet',
    private: 'Privat',
};

const ProductsPage: React.FC = () => {
    const notify = useNotify();
    const [products, setProducts] = useState<Product[]>([]);
    const [courses, setCourses] = useState<CourseOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<Product | null>(null);
    const [form, setForm] = useState<ProductFormState>(EMPTY_FORM);

    const loadProducts = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const { products: list } = await productService.getProducts({ itemsPerPage: 100 });
            setProducts(list);
        } catch (err) {
            console.error(err);
            setError('Die Produkte konnten nicht geladen werden.');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadCourses = useCallback(async () => {
        try {
            const response = await api.get<{ member?: CourseOption[]; 'hydra:member'?: CourseOption[] }>(
                '/courses?itemsPerPage=200',
                { headers: { Accept: 'application/ld+json' } }
            );
            setCourses(response.data.member ?? response.data['hydra:member'] ?? []);
        } catch (err) {
            console.error(err);
            notify('Die Kursliste konnte nicht geladen werden.', { type: 'warning' });
        }
    }, [notify]);

    useEffect(() => {
        loadProducts();
        loadCourses();
    }, [loadProducts, loadCourses]);

    const defaultPrice = (product: Product): ProductPrice | null => productService.getDefaultPrice(product);

    const openCreateDialog = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setDialogOpen(true);
    };

    const openEditDialog = (product: Product) => {
        const price = defaultPrice(product);
        setEditing(product);
        setForm({
            courseId: product.referenceId ?? '',
            title: product.title,
            shortDescription: product.shortDescription ?? '',
            description: product.description ?? '',
            visibility: product.visibility,
            isActive: product.isActive,
            priceType: price?.priceType === 'free' ? 'free' : 'one_time',
            amount: price?.amount ?? '0.00',
        });
        setDialogOpen(true);
    };

    const handleCourseChange = (courseId: number) => {
        const course = courses.find((c) => c.id === courseId);
        setForm((prev) => ({
            ...prev,
            courseId,
            title: prev.title.trim() === '' && course ? course.title : prev.title,
        }));
    };

    const normalizedAmount = (): string => {
        if (form.priceType === 'free') {
            return '0.00';
        }
        const parsed = parseFloat(form.amount.replace(',', '.'));
        return Number.isFinite(parsed) ? parsed.toFixed(2) : '0.00';
    };

    const validate = (): string | null => {
        if (!form.title.trim()) {
            return 'Bitte einen Titel angeben.';
        }
        if (!editing && form.courseId === '') {
            return 'Bitte einen Kurs auswählen.';
        }
        if (form.priceType === 'one_time' && parseFloat(normalizedAmount()) <= 0) {
            return 'Ein Einmalpreis muss größer als 0 sein. Für kostenlose Produkte bitte "Kostenlos" wählen.';
        }
        return null;
    };

    const savePrice = async (product: Product) => {
        const existing = defaultPrice(product);
        const payload = {
            priceType: form.priceType,
            amount: normalizedAmount(),
            currency: 'EUR',
            isDefault: true,
            isActive: true,
        };

        if (existing) {
            await productService.updatePrice(existing.id, payload);
            return;
        }

        await productService.createPrice(product.id, payload);
    };

    const handleSave = async () => {
        const validationError = validate();
        if (validationError) {
            notify(validationError, { type: 'warning' });
            return;
        }

        try {
            setSaving(true);

            const common = {
                title: form.title.trim(),
                shortDescription: form.shortDescription.trim() || undefined,
                description: form.description.trim() || undefined,
                visibility: form.visibility,
                isPublic: form.visibility === 'public',
                isActive: form.isActive,
            };

            const product = editing
                ? await productService.updateProduct(editing.id, common)
                : await productService.createProductFromCourse(Number(form.courseId), form.title, common);

            await savePrice(editing ?? product);

            notify(editing ? 'Produkt aktualisiert.' : 'Produkt angelegt.', { type: 'success' });
            setDialogOpen(false);
            await loadProducts();
        } catch (err) {
            console.error(err);
            notify('Das Produkt konnte nicht gespeichert werden.', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (product: Product) => {
        if (!window.confirm(`Produkt "${product.title}" wirklich löschen?`)) {
            return;
        }

        try {
            await productService.deleteProduct(product.id);
            notify('Produkt gelöscht.', { type: 'success' });
            await loadProducts();
        } catch (err) {
            console.error(err);
            notify('Das Produkt konnte nicht gelöscht werden. Bereits verkaufte Produkte lassen sich nur deaktivieren.', {
                type: 'error',
            });
        }
    };

    const renderPrice = (product: Product) => {
        const price = defaultPrice(product);
        if (!price) {
            return <Chip size="small" color="warning" label="Kein Preis" />;
        }
        return <Chip size="small" label={productService.formatPrice(price)} />;
    };

    const courseTitle = (product: Product): string => {
        const course = courses.find((c) => c.id === product.referenceId);
        return course?.title ?? `Kurs #${product.referenceId ?? '—'}`;
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InventoryIcon fontSize="large" />
                        Produkte
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Produkte sind die verkaufbaren Artikel Ihrer Landing-Pages. Jedes Produkt verweist auf einen Kurs
                        und bekommt einen Preis.
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
                    Produkt aus Kurs erstellen
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Card>
                <CardContent>
                    {loading ? (
                        <>
                            <Skeleton height={48} />
                            <Skeleton height={48} />
                            <Skeleton height={48} />
                        </>
                    ) : products.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography variant="h6" gutterBottom>
                                Noch keine Produkte
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Legen Sie ein Produkt aus einem Kurs an, um es auf einer Landing-Page verkaufen zu können.
                            </Typography>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
                                Produkt aus Kurs erstellen
                            </Button>
                        </Box>
                    ) : (
                        <TableContainer component={Paper} elevation={0}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Titel</TableCell>
                                        <TableCell>Kurs</TableCell>
                                        <TableCell>Preis</TableCell>
                                        <TableCell>Sichtbarkeit</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Aktionen</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {products.map((product) => (
                                        <TableRow key={product.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {product.title}
                                                </Typography>
                                                {product.shortDescription && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {product.shortDescription}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>{courseTitle(product)}</TableCell>
                                            <TableCell>{renderPrice(product)}</TableCell>
                                            <TableCell>{VISIBILITY_LABELS[product.visibility]}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    color={product.isActive ? 'success' : 'default'}
                                                    label={product.isActive ? 'Aktiv' : 'Inaktiv'}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Bearbeiten">
                                                    <IconButton aria-label="Bearbeiten" onClick={() => openEditDialog(product)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Löschen">
                                                    <IconButton aria-label="Löschen" onClick={() => handleDelete(product)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? 'Produkt bearbeiten' : 'Produkt aus Kurs erstellen'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth disabled={Boolean(editing)}>
                            <InputLabel id="product-course-label">Kurs</InputLabel>
                            <Select
                                labelId="product-course-label"
                                label="Kurs"
                                value={form.courseId}
                                onChange={(e) => handleCourseChange(Number(e.target.value))}
                            >
                                {courses.map((course) => (
                                    <MenuItem key={course.id} value={course.id}>
                                        {course.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Titel"
                            fullWidth
                            required
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                        />

                        <TextField
                            label="Kurzbeschreibung"
                            fullWidth
                            value={form.shortDescription}
                            onChange={(e) => setForm({ ...form, shortDescription: e.target.value })}
                            helperText="Wird auf der Landing-Page unter dem Titel angezeigt."
                        />

                        <TextField
                            label="Beschreibung"
                            fullWidth
                            multiline
                            rows={3}
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                        />

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <FormControl fullWidth>
                                <InputLabel id="product-price-type-label">Preismodell</InputLabel>
                                <Select
                                    labelId="product-price-type-label"
                                    label="Preismodell"
                                    value={form.priceType}
                                    onChange={(e) =>
                                        setForm({ ...form, priceType: e.target.value as 'one_time' | 'free' })
                                    }
                                >
                                    <MenuItem value="one_time">Einmalig</MenuItem>
                                    <MenuItem value="free">Kostenlos</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                label="Preis"
                                fullWidth
                                disabled={form.priceType === 'free'}
                                value={form.priceType === 'free' ? '0.00' : form.amount}
                                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">EUR</InputAdornment>,
                                }}
                            />
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                            <FormControl fullWidth>
                                <InputLabel id="product-visibility-label">Sichtbarkeit</InputLabel>
                                <Select
                                    labelId="product-visibility-label"
                                    label="Sichtbarkeit"
                                    value={form.visibility}
                                    onChange={(e) =>
                                        setForm({ ...form, visibility: e.target.value as ProductVisibility })
                                    }
                                >
                                    {(Object.keys(VISIBILITY_LABELS) as ProductVisibility[]).map((value) => (
                                        <MenuItem key={value} value={value}>
                                            {VISIBILITY_LABELS[value]}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.isActive}
                                        onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                                    />
                                }
                                label="Aktiv"
                                sx={{ flexShrink: 0 }}
                            />
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProductsPage;
